<template>
  <div class="EmployeesView animated fadeIn">
    <MuiGridContainer
      class="TemplateDefault__container"
    >
      <div class="row pt-6 pb-4">
        <div class="col-md-6 pb-4">
          <h1 class="font-semibold font-xl">
            {{ $t('Admin.employees') }}
          </h1>
        </div>
        <div class="col-md-6 pb-4 d-flex justify-content-start justify-content-md-end">
          <MuiButtonFlat
            size="m"
            @click.native="showModalEmployees"
          >
            {{ $t('Admin.inviteEmployees') }}
          </MuiButtonFlat>
        </div>
      </div>
      <div class="row text-right">
        <div class="col">
          <MuiToggleButton
            v-model="isInvitesTable"
            :size="24"
            :left-label="$t('Admin.pendingEmployees')"
            name="Employees"
          />
        </div>
      </div>
      <Transition name="slide-fade">
        <MuiAlgoliaList
          v-if="!employeesStatus.DEACTIVATE_IN"
          :key="algoliaIndex"
          :filters="algoliaFilters"
          :facets="algoliaFacets"
          :table-config="algoliaConfiguration"
          :show-filters="false"
          :index="algoliaIndex"
          empty-message="No results found"
          class="TemplateDefault__AlgoliaList animated fadeIn"
        />
        <div
          v-else
          class="row"
        >
          <div class="col-12 col-md-6">
            <div class="TemplateDefault__LoadingMessage">
              Reloading Algolia List ...
            </div>
          </div>
        </div>
      </Transition>
    </MuiGridContainer>
  </div>
</template>
<script>

import {
  mapState,
  mapActions,
  mapMutations,
} from 'vuex';
import _ from 'lodash';
import {
  MuiAlgoliaList,
  MuiButtonFlat,
  MuiGridContainer,
  MuiToggleButton,
  RECORD_TYPES,
} from '@ubeeqo/motion-ui';
import MAIN_VIEW_MODEL from '@/views/MAIN_VIEW_MODEL';
import VIEW_MODEL from '@/store/modules/VIEW_MODEL';
import EmployeesModal from './components/EmployeesModalComponent';
import ActionTableComponent from './components/ActionTableComponent';
import EmployeesDeactivateModal from './components/EmployeesDeactivateModalComponent';

import { BADGE_TYPE } from './constants';

const modalIdEmployees = 'EmployeesModal';
const modalIdDeactivate = 'EmployeesDeactivateModal';

export default {
  name: 'EmployeesView',
  components: {
    MuiGridContainer,
    MuiAlgoliaList,
    MuiButtonFlat,
    MuiToggleButton,
  },
  data() {
    return {
      showAlgoliaList: true,
      isInvitesTable: false,
    };
  },
  computed: {
    ...mapState(MAIN_VIEW_MODEL.userAccount, {
      userAccount: state => state.data,
    }),
    ...mapState(VIEW_MODEL.companyAdmin.employees, {
      employeesStatus: state => state.STATUS,
    }),
    algoliaIndex() {
      return this.isInvitesTable ? 'invites' : 'employees';
    },
    algoliaFilters() {
      return this.isInvitesTable
        ? `status:invited AND inviter_company_uuid:${this.userAccount.company.uuid}`
        : `company_uuid:${this.userAccount.company.uuid}`;
    },
    algoliaFacets() {
      return this.isInvitesTable
        ? []
        : [{
          type: 'refinementList',
          props: {
            title: 'Status',
            attributeName: 'dedicated_fleet_status',
            toggled: false,
          },
        }];
    },
    algoliaConfiguration() {
      return [
        {
          title: 'Employee',
          transformResult: result => (this.isInvitesTable ? '-' : `${result.user_first_name}  ${result.user_last_name}`),
          displayPriority: 1,
          columnRatio: 1.5,
        },
        {
          title: 'Email',
          attributeName: this.isInvitesTable ? 'invitee_email' : 'email',
          displayPriority: 1,
        },
        {
          title: 'Telephone',
          attributeName: 'phone',
          displayPriority: 1,
        },
        {
          title: 'Status',
          transformResult: result => (this.isInvitesTable ? 'Pending' : result.dedicated_fleet_status),
          type: RECORD_TYPES.badge,
          displayPriority: 1,
          classes: result => this.getBadgeClasses(result.dedicated_fleet_status),
        },
        {
          title: 'Action',
          type: RECORD_TYPES.component,
          component: ActionTableComponent,
          props: result => ({
            showAction: !this.isInvitesTable,
            startDeactivation: () => this.startDeactivation(result),
            isActivated: this.isActivated(result.dedicated_fleet_status),
          }),
          displayPriority: 1,
          columnRatio: 0.4,
        },
      ];
    },
    getComponentEmployeesModal: () => ({ component: EmployeesModal }),
  },
  destroyed() {
    this.RESET_STORE();
  },
  methods: {
    ...mapActions(VIEW_MODEL.companyAdmin.employees, [
      'cleanEmployeesEmails',
      'sendEmployeesEmails',
      'deactivateEmployees',
    ]),
    ...mapMutations(VIEW_MODEL.companyAdmin.employees, [
      'RESET_STORE',
    ]),
    getComponentEmployeesDeactivateModal(isActivation) {
      return {
        component: EmployeesDeactivateModal,
        props: {
          isActivation,
        },
      };
    },
    startDeactivation(data) {
      const employeeUuid = data.uuid;
      const employeeName = `${data.user_first_name} ${data.user_last_name}`;
      const isActivation = !this.isActivated(data.dedicated_fleet_status);
      const modalTitle = isActivation ? 'Activate Employee' : 'Deactivate Employee';

      this.$modal.show(modalIdDeactivate, {
        content: this.getComponentEmployeesDeactivateModal(isActivation),
        title: modalTitle,
        headerBgColor: 'secondary',
        newHeader: true,
        headerCrossBgColor: 'secondary',
        closeButton: false,
        cancelButton: {
          text: 'Cancel',
          action: () => this.$modal.close(modalIdDeactivate),
          type: 'ink',
        },
        acceptButton: {
          text: 'Accept',
          action: () => this.activationAction(employeeUuid, employeeName, isActivation),
          type: 'main',
        },
        size: 'small',
      });
    },
    getBadgeClasses(status) {
      const camelCaseStatus = _.camelCase(status);
      const badgeType = BADGE_TYPE[camelCaseStatus] || BADGE_TYPE.fallback;
      const badgeClasses = `mui-badge color-ink ${badgeType}`;
      return badgeClasses;
    },
    async activationAction(employeeUuid, employeeName, isActivation) {
      await this.deactivateEmployees({
        employeeUuid,
        isActivation,
        employeeName,
      });

      this.$modal.close(modalIdDeactivate);
    },
    showModalEmployees() {
      this.$modal.show(modalIdEmployees, {
        content: this.getComponentEmployeesModal,
        title: 'Add New Employees',
        headerBgColor: 'secondary',
        newHeader: true,
        headerCrossBgColor: 'secondary',
        closeButton: false,
        cancelButton: {
          text: 'Cancel',
          action: this.closeModalEmployees,
          type: 'ink',
        },
        acceptButton: {
          text: 'SEND',
          action: this.acceptModalEmployees,
          type: 'main',
        },
        size: 'small',
      });
    },
    closeModalEmployees() {
      if (
        this.employeesStatus.IDLE
        || this.employeesStatus.FORM_INVALID
        || this.employeesStatus.FORM_VALID
      ) {
        this.cleanEmployeesEmails();
        this.$modal.close(modalIdEmployees);
      }
    },
    async acceptModalEmployees() {
      // TODO: [Webapp][Brunel] Actually possible to send invitation to already existing emails
      await this.sendEmployeesEmails();
      this.$modal.close(modalIdEmployees);
      this.cleanEmployeesEmails();
    },
    isActivated(status) {
      return status && _.toLower(status) === 'activated';
    },
  },
};
</script>
